/**
 * AI analysis from the GUI: the service in use (mirrored from the core, which keeps the key),
 * the last suggestions, and which of them the user keeps. Kept here so the suggestions survive
 * the AI window being moved or closed.
 *
 * Nothing changes the graph until "Apply": the kept suggestions become one `graph.batch`
 * (descriptions → node notes, groups → new groups, review findings → comments on their nodes;
 * after a scan, "enrich" adds nodes for code no plugin read, categories, other kinds and
 * missing links), so one Ctrl+Z takes them all back.
 */
import { createSignal, type Accessor } from "solid-js";
import type { AiSettings } from "@protocol/AiSettings";
import type { AiSettingsInput } from "@protocol/AiSettingsInput";
import type { AiSuggestions } from "@protocol/AiSuggestions";
import type { AiTask } from "@protocol/AiTask";
import type { Graph } from "@protocol/Graph";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Actions } from "../client/actions";
import { isClientError, type Client } from "../client/client";
import { toast } from "../shared/toast";
import type { MenuSection, ToolbarButton } from "../shared/menus";

/** What is running: a task, the check, or saving the settings. */
export type AiBusy = AiTask | "test" | "save";

/** The service's answer to the check. */
export interface AiChecked {
  reply: string;
  ms: number;
}

/** The AI service. */
export interface Ai {
  /** The service in use (`null` until the core answered). */
  settings: Accessor<AiSettings | null>;
  /** Whether a service is chosen. */
  ready(): boolean;
  /** Chooses the service (a missing `api_key` keeps the saved one, `""` forgets it). */
  save(input: AiSettingsInput): Promise<boolean>;
  /** Asks the service a tiny question. */
  test(): Promise<boolean>;
  /** The answer to the last check. */
  checked: Accessor<AiChecked | null>;
  /** Asks for suggestions; they replace the previous ones, all kept. */
  run(task: AiTask): Promise<boolean>;
  /** What runs now, if anything. */
  busy: Accessor<AiBusy | null>;
  /** The last suggestions. */
  last: Accessor<AiSuggestions | null>;
  /** Whether a suggestion is kept (see {@link suggestionKeys}). */
  isKept(key: string): boolean;
  /** Keeps or drops one suggestion. */
  setKept(key: string, kept: boolean): void;
  /** Keeps or drops every suggestion. */
  setAllKept(kept: boolean): void;
  /** How many suggestions are kept. */
  keptCount(): number;
  /** Applies the kept suggestions in one undoable edit, then drops them from the list. */
  apply(): Promise<boolean>;
  /** Forgets the suggestions. */
  dismiss(): void;
  /** Why the last request failed, if it did. */
  error: Accessor<string | null>;
}

/** The key of a suggested description. */
export const noteKey = (node: string) => `note:${node}`;
/** The key of a suggested group. */
export const groupKey = (id: string) => `group:${id}`;
/** The key of a review finding (by position). */
export const findingKey = (index: number) => `finding:${index}`;
/** The key of a suggested new node. */
export const nodeKey = (id: string) => `node:${id}`;
/** The key of categories suggested for a node. */
export const categoriesKey = (node: string) => `categories:${node}`;
/** The key of another kind suggested for a node. */
export const kindKey = (node: string) => `kind:${node}`;
/** The key of a suggested link. */
export const linkKey = (l: AiSuggestions["links"][number]) => `link:${l.from}:${l.kind}:${l.to}`;

/** The keys of every suggestion that can be applied (findings about no node cannot). */
export function suggestionKeys(s: AiSuggestions): string[] {
  return [
    ...(s.nodes ?? []).map((n) => nodeKey(n.node.id)),
    ...s.notes.map((n) => noteKey(n.node)),
    ...s.groups.map((g) => groupKey(g.id)),
    ...(s.categories ?? []).map((c) => categoriesKey(c.node)),
    ...(s.kinds ?? []).map((k) => kindKey(k.node)),
    ...(s.links ?? []).map(linkKey),
    ...s.findings.flatMap((f, i) => (f.nodes.length ? [findingKey(i)] : [])),
  ];
}

/** How many suggestions there are (findings about no node too). */
export function suggestionCount(s: AiSuggestions): number {
  return s.notes.length + s.groups.length + s.findings.length + (s.nodes ?? []).length + (s.categories ?? []).length + (s.kinds ?? []).length + (s.links ?? []).length;
}

/** The comment a finding leaves on each of its nodes. */
export function findingComment(f: AiSuggestions["findings"][number]): string {
  const title = /[.!?]$/.test(f.title) ? f.title : `${f.title}.`;
  return f.detail ? `AI review (${f.level}): ${title} ${f.detail}` : `AI review (${f.level}): ${title}`;
}

/**
 * The edits applying the kept suggestions, in an order the core accepts: the plugin kinds they
 * use, new nodes, groups (parents first, as the core sent them), changes to nodes, then links.
 * A kept group whose suggested parent was dropped goes into the nearest kept (or existing)
 * group above it; what names a dropped new node (a member, categories, a link end) is left out.
 * `graph` is the graph now (for the categories nodes already have).
 */
export function editsFor(s: AiSuggestions, kept: (key: string) => boolean, graph?: Pick<Graph, "nodes">): GraphEdit[] {
  const edits: GraphEdit[] = [];
  const suggestedNodes = new Map((s.nodes ?? []).map((n) => [n.node.id, n.node]));
  /** Whether node `id` is there once applied: not a suggested node, or a kept one. */
  const there = (id: string) => !suggestedNodes.has(id) || kept(nodeKey(id));
  const keptKinds = (s.kinds ?? []).filter((k) => kept(kindKey(k.node)));
  const keptLinks = (s.links ?? []).filter((l) => kept(linkKey(l)) && there(l.from) && there(l.to));
  const keptNodes = (s.nodes ?? []).filter((n) => kept(nodeKey(n.node.id)));
  const usedNodeKinds = new Set([...keptKinds.map((k) => k.kind), ...keptNodes.map((n) => n.node.kind)]);
  const usedLinkKinds = new Set(keptLinks.map((l) => l.kind));
  const define = { nodes: (s.define?.nodes ?? []).filter((k) => usedNodeKinds.has(k.id)), links: (s.define?.links ?? []).filter((k) => usedLinkKinds.has(k.id)) };
  if (define.nodes.length || define.links.length) edits.push({ edit: "define_kinds", kinds: define });
  for (const n of keptNodes) edits.push({ edit: "add_node", node: n.node, links: [] });
  for (const n of s.notes) {
    if (kept(noteKey(n.node))) edits.push({ edit: "update_node", id: n.node, patch: { note: n.note } });
  }
  const suggested = new Map(s.groups.map((g) => [g.id, g]));
  /** The group `g` goes into once dropped suggestions are skipped. */
  const parentOf = (parent: string | null): string | null => {
    const seen = new Set<string>();
    while (parent && suggested.has(parent) && !kept(groupKey(parent)) && !seen.has(parent)) {
      seen.add(parent);
      parent = suggested.get(parent)!.parent;
    }
    return parent;
  };
  for (const g of s.groups) {
    if (!kept(groupKey(g.id))) continue;
    edits.push({ edit: "add_group", id: g.id, name: g.name, parent: parentOf(g.parent), members: g.members.filter(there), note: g.note });
    for (const inside of g.groups ?? []) edits.push({ edit: "update_group", id: inside, patch: { parent: g.id } });
  }
  for (const c of s.categories ?? []) {
    if (!kept(categoriesKey(c.node)) || !there(c.node)) continue;
    const has = graph?.nodes.find((n) => n.id === c.node)?.categories ?? suggestedNodes.get(c.node)?.categories ?? [];
    edits.push({ edit: "update_node", id: c.node, patch: { categories: [...has, ...c.categories] } });
  }
  for (const k of keptKinds) {
    edits.push({ edit: "update_node", id: k.node, patch: { kind: k.kind } });
  }
  for (const l of keptLinks) edits.push({ edit: "add_link", link: { from: l.from, to: l.to, kind: l.kind, label: "" } });
  s.findings.forEach((f, i) => {
    if (!kept(findingKey(i))) return;
    for (const node of f.nodes) edits.push({ edit: "add_comment", id: node, text: findingComment(f) });
  });
  return edits;
}

/** The suggestions left once the applied ones are taken out. */
function withoutApplied(s: AiSuggestions, applied: (key: string) => boolean): AiSuggestions {
  return {
    ...s,
    notes: s.notes.filter((n) => !applied(noteKey(n.node))),
    groups: s.groups.filter((g) => !applied(groupKey(g.id))),
    findings: s.findings.filter((_, i) => !applied(findingKey(i))),
    nodes: (s.nodes ?? []).filter((n) => !applied(nodeKey(n.node.id))),
    categories: (s.categories ?? []).filter((c) => !applied(categoriesKey(c.node))),
    kinds: (s.kinds ?? []).filter((k) => !applied(kindKey(k.node))),
    links: (s.links ?? []).filter((l) => !applied(linkKey(l))),
  };
}

/**
 * Starts mirroring the AI settings through `client`; edits go through `actions`; `graph` is the
 * graph now (for the categories nodes already have).
 */
export function createAi(client: Client, actions: Actions, graph: () => Pick<Graph, "nodes"> = () => ({ nodes: [] })): Ai {
  const [settings, setSettings] = createSignal<AiSettings | null>(null);
  const [checked, setChecked] = createSignal<AiChecked | null>(null);
  const [busy, setBusy] = createSignal<AiBusy | null>(null);
  const [last, setLast] = createSignal<AiSuggestions | null>(null);
  const [kept, setKeptSet] = createSignal<ReadonlySet<string>>(new Set<string>());
  const [error, setError] = createSignal<string | null>(null);

  client.onOpen(() => {
    client
      .request({ method: "ai.settings" })
      .then((r) => r.type === "ai_settings" && setSettings(r.settings))
      .catch(() => {});
  });
  client.onEvent((event) => {
    if (event.event === "ai.settings_changed") setSettings(event.data.settings);
  });

  /** Runs one request as `what`, keeping the reason if it fails. */
  const attempt = async (what: AiBusy, work: () => Promise<void>): Promise<boolean> => {
    if (busy()) return false;
    setBusy(what);
    setError(null);
    try {
      await work();
      return true;
    } catch (e) {
      setError(isClientError(e) ? e.message : String(e));
      return false;
    } finally {
      setBusy(null);
    }
  };

  return {
    settings,
    ready: () => !!settings()?.base_url,
    save: (input) =>
      attempt("save", async () => {
        const r = await client.request({ method: "ai.set_settings", params: { settings: input } });
        if (r.type === "ai_settings") setSettings(r.settings);
        setChecked(null);
        toast("AI service saved.");
      }),
    test: () =>
      attempt("test", async () => {
        setChecked(null);
        const r = await client.request({ method: "ai.test" });
        if (r.type === "ai_checked") setChecked({ reply: r.reply, ms: r.ms });
      }),
    checked,
    run: (task) =>
      attempt(task, async () => {
        const r = await client.request({ method: "ai.suggest", params: { task } });
        if (r.type !== "ai_suggestions") return;
        setLast(r.suggestions);
        setKeptSet(new Set(suggestionKeys(r.suggestions)));
      }),
    busy,
    last,
    isKept: (key) => kept().has(key),
    setKept(key, on) {
      const next = new Set(kept());
      if (on) next.add(key);
      else next.delete(key);
      setKeptSet(next);
    },
    setAllKept(on) {
      const s = last();
      setKeptSet(new Set(on && s ? suggestionKeys(s) : []));
    },
    keptCount: () => {
      const s = last();
      return s ? suggestionKeys(s).filter((k) => kept().has(k)).length : 0;
    },
    async apply() {
      const s = last();
      if (!s) return false;
      const keys = kept();
      const edits = editsFor(s, (k) => keys.has(k), graph());
      if (!edits.length) return false;
      const changes = await actions.batch(edits);
      if (!changes) return false;
      setLast(withoutApplied(s, (k) => keys.has(k)));
      setKeptSet(new Set<string>());
      toast(`Applied ${keys.size} suggestion${keys.size === 1 ? "" : "s"}. Ctrl+Z takes them back.`);
      return true;
    },
    dismiss() {
      setLast(null);
      setKeptSet(new Set<string>());
    },
    error,
  };
}

/** The Tools menu's AI. */
export function aiMenus(): MenuSection[] {
  return [{ menu: "tools", section: "AI", order: 20, entries: () => [{ label: "AI suggestions…", command: "window.open", argument: "ai" }] }];
}

/** The toolbar's AI. */
export function aiToolbar(): ToolbarButton[] {
  return [{ id: "tool-ai", section: "tools", order: 40, icon: "ai", label: "AI", title: "Notes, groups, a review or an enrich, by your AI service", command: "window.open", argument: "ai" }];
}
